import React, {useEffect, useRef, useState} from "react";


import {
    fetchInternalBranchList,
    fetchInternalEmployeeList,
    fetchProductList,
} from "../../store/list-slice";

import Button from "../../components/common/button/Button";
import SubHeader from "../../components/header/SubHeader";
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {createInternalOrder, fetchInternalOrderById, updateInternalOrder} from "../../store/order-internal-slice";
import SelectOption from "../../components/common/form/SelectOption";
import moment from "moment-timezone";
import {BranchType} from "../../enums/enums";

const timezone = 'Asia/Kolkata';
const AddInternalOrder = () => {
    const dispatch = useDispatch();
    const params = useParams();
    const navigator = useNavigate();
    const [purchaseEdit, setPurchaseEdit] = useState(null);
    const initialQuantityRef = useRef({});

    const [productsToPurchase, setProductsToPurchase] = useState([{
        product: '',
        quantity: '',
    }]);

    const {error} = useSelector((state) => state.internalOrder)

    //Basic Form error
    const [errors, setErrors] = useState({
        internalBranchName: '',
        purchaseDate: '',
    });

    //Product errors
    const [productErrors, setProductErrors] = useState([
        {product: '', quantity: ''}
    ]);

    const handleBlur = (e) => {
        const {id, value} = e.target;
        validateField(id, value);
    };

    const validateField = (id, value) => {
        let error = "";
        const trimmed = typeof value === "string" ? value.trim() : value;
        switch (id) {
            case "internalBranchName":
                if (userRole?.branchType === BranchType.Main && !trimmed) {
                    error = "Internal branch is required";
                }
                break;

            case "purchaseDate":
                if (!trimmed) {
                    error = "Initiated date is required";
                }
                break;

            default:
                break;
        }

        setErrors((prev) => ({...prev, [id]: error}));
        return error === "";
    };

    //Validate row of product
    const validateProductRow = (product, index) => {
        const errors = {};

        if (!product.product) {
            errors.product = 'Product is required';
        }

        if (!product.quantity) {
            errors.quantity = 'Quantity is required';
        } else {
            const quantity = parseFloat(product.quantity);
            const baseStock = updatedProductListStock?.[product.product] ?? 0;

            let availableStock = baseStock;

            if (purchaseEdit) {
                const initialQuantity = parseFloat(initialQuantityRef.current?.[product.product]) || 0;
                availableStock += initialQuantity;
            }

            if (!isNaN(quantity) && quantity > availableStock) {
                errors.quantity = 'Quantity exceeds available stock';
            }
        }

        setProductErrors(prev => {
            const newErrors = [...prev];
            newErrors[index] = errors;
            return newErrors;
        });

        return Object.keys(errors).length === 0;
    };


    const validateAllProducts = () => {
        return productsToPurchase.map((product, index) =>
            validateProductRow(product, index)
        ).every(valid => valid);
    };

    const validateForm = () => {
        let isValid = true;
        Object.entries(formData).forEach(([key, value]) => {
            const valid = validateField(key, value);
            if (!valid) isValid = false;
        });
        return isValid;
    };

    const handleProductBlur = (index, field) => {
        const product = productsToPurchase[index];
        validateProductRow(product, index);
    };


    const {items, createStatus, updateStatus} = useSelector(state => state.internalOrder);
    const userRole = useSelector((state) => state.auth.selectedRole);
    const list = useSelector(state => state.list);
    const {items: internalBranch = []} = list.internalBranch || {};
    const {items: employee = []} = list.internalEmployee || {};

    const {
        items: productList,
        itemsWithTags
    } = list.product;

    const updatedProductListStock = itemsWithTags.reduce((acc, item) => {
        acc[item._id] = item.stock;
        return acc;
    }, {});

    const [formData, setFormData] = useState({
        id: userRole.scopeId,
        internalBranchName: '',
        employee: '',
        purchaseDate: moment().tz(timezone).format('YYYY-MM-DD'), // default to today
        products: [],
        note: '',
    });

    useEffect(() => {
        if (params.interOrderId) {
            dispatch(fetchInternalOrderById({...userRole, id: params.interOrderId}));
        }
    }, [dispatch, params.interOrderId, userRole]);


    useEffect(() => {
        if (params.interOrderId && items.length > 0) {
            const found = items?.find(item => item.id === params.interOrderId);
            if (found) {
                setPurchaseEdit(found);
                // Pre-fill form data
                setFormData(prevState => ({
                    ...prevState,
                    id: found._id,
                    internalBranchName: found.internalBranchName,
                    purchaseDate: found.purchaseDate ? moment(found.purchaseDate).tz(timezone).format('YYYY-MM-DD') : '',
                    employee: found.employee,
                    products: found.products || [],
                    note: found.note || '',
                }));
                setProductsToPurchase(found.products);
            }
        }
    }, [params.interOrderId, items]);

    const handleFormChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleProductChange = (index, field, value) => {
        setProductsToPurchase(prev => {
            const updated = [...prev];
            updated[index] = {...updated[index], [field]: value};
            return updated;
        });
    };

    const handleAddRow = () => {
        setProductsToPurchase(prev => [...prev, {product: '', quantity: ''}]);
        setProductErrors(prev => [...prev, {product: '', quantity: ''}]);
    };

    const handleRemoveRow = (index) => {
        setProductsToPurchase(prev => prev.filter((_, i) => i !== index));
        setProductErrors(prev => prev.filter((_, i) => i !== index));
    };

    const handleDateChange = (e) => {
        const {name, value} = e.target;
        if (name === "purchaseDate") {
            if (!value) {
                // If input is cleared, remove date
                setFormData((prev) => ({
                    ...prev,
                    purchaseDate: "",
                }));
                return;
            }
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const isFormValid = validateForm();
        const areProductsValid = validateAllProducts();

        if (!isFormValid || !areProductsValid) return;

        delete formData.name;
        // Build data object
        const data = {
            id: userRole.scopeId,
            purchaseDate: formData.purchaseDate,
            products: productsToPurchase?.some(item => Object.values(item).some(Boolean)) ? productsToPurchase : [],
            note: formData.note || '',
        };

        // Include internalBranchName and employee only for Main branch type
        if (userRole?.branchType === BranchType.Main) {
            if (formData.internalBranchName) data.internalBranchName = formData.internalBranchName;
            if (formData.employee) data.employee = formData.employee;
        }

        if (purchaseEdit) {
            const updatedData = {
                ...data,
                _id: params.interOrderId
            };
            // Update the internal order
            dispatch(updateInternalOrder({...userRole, data: updatedData}));
        } else {
            // Create the internal order
            dispatch(createInternalOrder({...userRole, data}));
        }
    };


    useEffect(() => {
        if (createStatus === "success") {
            navigator('../');
        }
        if (updateStatus === "success") {
            navigator('../../');
        }
    }, [createStatus, updateStatus, navigator]);

    useEffect(() => {
        if (formData.internalBranchName) {
            dispatch(fetchInternalEmployeeList({...userRole, branchId: formData.internalBranchName}));
        }
    }, [dispatch, userRole, formData.internalBranchName])


    useEffect(() => {
        dispatch(fetchProductList({...userRole, internalProducts: true}));
        dispatch(fetchInternalBranchList(userRole));
    }, [dispatch, userRole]);

    return (
        <>
            <SubHeader title={`${purchaseEdit ? 'Update Internal Order' : 'Add Internal Order'}`}>
                {purchaseEdit ? <Button to="../../" className="btn-sm bg-secondary bg-opacity-50">Back</Button> :
                    <Button to="../" className="btn-sm bg-secondary bg-opacity-50">Back</Button>}
            </SubHeader>
            {error && <p className="text-danger fs-md fw-semibold">{error}</p>}
            <form onSubmit={handleSubmit}>
                <div className="row gap-6">
                    <div className="col-12 col-lg-8 col-xl-8">
                        <div className="row align-items-start g-4">
                            {/*Internal Branch Name*/}
                            {userRole?.branchType === BranchType.Main && <div className="col-md-4">
                                <div className="form-group">
                                    <label className="form-label opacity-75">Internal Branch</label>
                                    <SelectOption
                                        name="internalBranchName"
                                        className="form-select"
                                        id="internalBranchName"
                                        value={formData.internalBranchName}
                                        onBlur={handleBlur}
                                        options={internalBranch}
                                        disabled={purchaseEdit}
                                        onChange={(e) => handleFormChange("internalBranchName", e.target.value)}
                                    />
                                    {errors.internalBranchName &&
                                        <div className="invalid-feedback d-block">{errors.internalBranchName}</div>}
                                </div>
                            </div>}
                            {/*Employee*/}
                            {userRole?.branchType === BranchType.Main && <div className="col-md-4">
                                <div className="form-group">
                                    <label className="form-label opacity-75">Employee</label>
                                    <SelectOption
                                        className="form-select"
                                        id="employee"
                                        name="employee"
                                        value={formData.employee}
                                        onBlur={handleBlur}
                                        options={employee}
                                        onChange={(e) => handleFormChange("employee", e.target.value)}
                                    />
                                    {errors.employee &&
                                        <div className="invalid-feedback d-block">{errors.employee}</div>}
                                </div>
                            </div>}
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label className="form-label opacity-75">Initiated On</label>
                                    <input
                                        className="form-control"
                                        id="purchaseDate"
                                        name="purchaseDate"
                                        type="date"
                                        onBlur={handleBlur}
                                        value={moment(formData.purchaseDate).tz(timezone).format('YYYY-MM-DD')}
                                        onChange={handleDateChange}
                                    />
                                    {errors.purchaseDate &&
                                        <div className="invalid-feedback d-block">{errors.purchaseDate}</div>}
                                </div>
                            </div>
                            {/*Notes*/}
                            <div className="col-md-8">
                                <div>
                                    <label className="form-label opacity-75">Note</label>
                                    <textarea
                                        className="form-control"
                                        name="note"
                                        rows="1"
                                        type="textarea"
                                        value={formData.note}
                                        onChange={(e) => handleFormChange("note", e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    {/*Add Products*/}
                    <div className="col-lg-12">
                        <div className="d-flex justify-content-between">
                            <h6 className="fw-md fs-6">Add Stock</h6>
                        </div>
                        {productsToPurchase.map((item, index) => {
                            // Set initial quantity once per product
                            if (purchaseEdit && initialQuantityRef.current[item.product] == null) {
                                initialQuantityRef.current[item.product] = Number(item.quantity || 0);
                            }
                            return (
                                <div key={index} className="row mb-3 mt-3 row-gap-2 card-sm">
                                    <div className="col-lg-3">
                                        {/* Product Select */}
                                        <SelectOption
                                            className="form-select"
                                            name="product"
                                            value={item.product}
                                            options={productList}
                                            isSearch={true}
                                            onBlur={() => handleProductBlur(index, "product")}
                                            onChange={(e) => handleProductChange(index, "product", e.target.value)}
                                        />
                                        {productErrors[index]?.product && (
                                            <div
                                                className="invalid-feedback d-block">{productErrors[index].product}</div>
                                        )}
                                    </div>

                                    <div className="col-lg-2">
                                        {/* Quantity */}
                                        <input
                                            className="form-control"
                                            name="quantity"
                                            type="text"
                                            placeholder="Enter quantity"
                                            value={item.quantity}
                                            onBlur={() => handleProductBlur(index, "quantity")}
                                            onChange={(e) => handleProductChange(index, "quantity", e.target.value)}
                                        />
                                        {productErrors[index]?.quantity && (
                                            <div
                                                className="invalid-feedback d-block">{productErrors[index].quantity}</div>
                                        )}
                                    </div>
                                    <div className="col-lg-2">
                                        {/* In Stock  */}
                                        <div className="position-relative">
                                            <input
                                                className="form-control text-secondary"
                                                name="stock"
                                                type="text"
                                                value={
                                                    updatedProductListStock?.[item.product] != null
                                                        ? Math.max(
                                                            0,
                                                            updatedProductListStock[item.product] +
                                                            (purchaseEdit ? initialQuantityRef.current[item.product] : 0)
                                                        )
                                                        : "-"
                                                }
                                                disabled
                                            />
                                            <span className="position-absolute end-0 top-0 h-100 pe-3 text-secondary"
                                                  style={{paddingTop: 6}}>
                                           In Stock
                                            </span>
                                        </div>
                                    </div>

                                    <div className="col-lg-3">
                                        {/* Add/Remove Buttons */}
                                        <button className="btn btn-outline-success me-2" type="button"
                                                onClick={handleAddRow}>
                                            +
                                        </button>
                                        {productsToPurchase.length > 1 && (
                                            <button
                                                className="btn btn-outline-danger"
                                                type="button"
                                                onClick={() => handleRemoveRow(index)}
                                            >
                                                −
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                        <button
                            type="submit"
                            className="btn btn-primary mt-3"
                            disabled={createStatus === 'loading' || updateStatus === 'loading'}
                        >Submit
                        </button>
                    </div>
                </div>
            </form>
        </>
    )
}

export default AddInternalOrder;
